import React from 'react';
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';
import {
  Bell,
  FileText,
  Calendar,
  Clock,
  Info,
  CheckCircle,
  ShoppingCart,
  TrendingUp,
  CreditCard,
  Briefcase,
  Users,
  UserPlus,
  UserMinus,
  PlusSquare,
  MinusSquare,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  Sliders,
  CheckSquare,
} from 'react-feather';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '@Apis/notification';

const currencyFormatter = new Intl.NumberFormat('vi-VN');
const quantityFormatter = new Intl.NumberFormat('vi-VN');

const formatCurrency = (value) => {
  const normalizedValue = Number(value || 0);

  return `${currencyFormatter.format(normalizedValue)} đ`;
};

const formatQuantity = (value) => {
  return quantityFormatter.format(Number(value || 0));
};

const isAdminDailyReport = (notification) => {
  if (!notification) {
    return false;
  }

  if (notification.type === 'admin_daily_report') {
    return true;
  }

  return Boolean(
    notification.summary ||
      notification.bank_transfer_amount ||
      notification.cod_paid_amount ||
      (Array.isArray(notification.team_rankings) && notification.team_rankings.length > 0) ||
      notification.warehouse_report ||
      notification.employee_report,
  );
};

const getMetricIconAndColor = (label) => {
  const normalized = label.toLowerCase();

  if (
    normalized.includes('tổng số đơn') ||
    normalized.includes('số đơn') ||
    normalized.includes('đơn')
  ) {
    return {
      icon: <ShoppingCart size={18} className="text-primary" />,
      bgColor: '#e3f2fd',
      textColor: '#1e88e5',
    };
  }
  if (
    normalized.includes('tổng số tiền') ||
    normalized.includes('doanh số') ||
    normalized.includes('doanh thu') ||
    normalized.includes('tiền')
  ) {
    return {
      icon: <TrendingUp size={18} style={{ color: '#ff9800' }} />,
      bgColor: '#fff3e0',
      textColor: '#f57c00',
    };
  }
  if (normalized.includes('chuyển khoản') || normalized.includes('khoản')) {
    return {
      icon: <CreditCard size={18} className="text-success" />,
      bgColor: '#e8f5e9',
      textColor: '#388e3c',
    };
  }
  if (normalized.includes('cod')) {
    return {
      icon: <Briefcase size={18} style={{ color: '#8b5cf6' }} />,
      bgColor: '#f3e5f5',
      textColor: '#7b1fa2',
    };
  }
  if (normalized.includes('làm việc') || normalized.includes('working')) {
    return {
      icon: <Users size={18} className="text-info" />,
      bgColor: '#e0f7fa',
      textColor: '#0097a7',
    };
  }
  if (normalized.includes('mới')) {
    return {
      icon: <UserPlus size={18} className="text-success" />,
      bgColor: '#e8f5e9',
      textColor: '#388e3c',
    };
  }
  if (
    normalized.includes('nghỉ') ||
    normalized.includes('nghĩ') ||
    normalized.includes('trái') ||
    normalized.includes('left')
  ) {
    return {
      icon: <UserMinus size={18} className="text-danger" />,
      bgColor: '#ffebee',
      textColor: '#d32f2f',
    };
  }
  if (normalized.includes('nhập')) {
    if (normalized.includes('tổng sl') || normalized.includes('số lượng')) {
      return {
        icon: <PlusSquare size={18} className="text-success" />,
        bgColor: '#e8f5e9',
        textColor: '#388e3c',
      };
    }
    return {
      icon: <ArrowDownLeft size={18} className="text-primary" />,
      bgColor: '#e3f2fd',
      textColor: '#1e88e5',
    };
  }
  if (normalized.includes('xuất')) {
    if (normalized.includes('tổng sl') || normalized.includes('số lượng')) {
      return {
        icon: <MinusSquare size={18} className="text-warning" />,
        bgColor: '#fff3e0',
        textColor: '#f57c00',
      };
    }
    return {
      icon: <ArrowUpRight size={18} className="text-warning" />,
      bgColor: '#fff3e0',
      textColor: '#f57c00',
    };
  }
  if (normalized.includes('điều chuyển')) {
    return {
      icon: <RefreshCw size={18} className="text-info" />,
      bgColor: '#e0f7fa',
      textColor: '#0097a7',
    };
  }
  if (normalized.includes('điều chỉnh')) {
    return {
      icon: <Sliders size={18} style={{ color: '#8b5cf6' }} />,
      bgColor: '#f3e5f5',
      textColor: '#7b1fa2',
    };
  }
  if (normalized.includes('ghi sổ') || normalized.includes('posted')) {
    return {
      icon: <CheckSquare size={18} className="text-success" />,
      bgColor: '#e8f5e9',
      textColor: '#388e3c',
    };
  }

  // Fallback
  return {
    icon: <FileText size={18} className="text-secondary" />,
    bgColor: '#f3f4f6',
    textColor: '#374151',
  };
};

const parseNotificationMessage = (message) => {
  if (!message) return null;

  // Format if it's a pipe-separated stats list
  if (message.includes('|')) {
    const parts = message.split('|').map((p) => p.trim());
    return (
      <div className="row mt-1">
        {parts.map((part) => {
          let label = part;
          let value = '';
          if (part.includes(':')) {
            const subparts = part.split(':');
            label = subparts[0].trim();
            value = subparts.slice(1).join(':').trim();
          }

          const { icon, bgColor } = getMetricIconAndColor(label);

          return (
            <div key={`${label}-${value || part}`} className="col-sm-6 col-12 mb-1">
              <div
                className="p-1 border rounded shadow-sm d-flex align-items-center notification-summary-card"
                style={{
                  minHeight: '75px',
                  paddingLeft: '15px',
                }}
              >
                <div
                  className="mr-1 d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{
                    width: '38px',
                    height: '38px',
                    backgroundColor: bgColor,
                  }}
                >
                  {icon}
                </div>
                <div>
                  <div
                    className="small text-muted font-weight-bold text-uppercase"
                    style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                  >
                    {label}
                  </div>
                  <div
                    className="h4 font-weight-bolder mt-25 mb-0"
                    style={{ color: '#fba92c', fontSize: '18px' }}
                  >
                    {value || '—'}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <p
      className="text-dark mb-0"
      style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7', fontSize: '15px' }}
    >
      {message}
    </p>
  );
};

const renderReportDetails = (notification) => {
  if (!isAdminDailyReport(notification)) {
    return null;
  }

  const summary = notification.summary || {};
  const teamRankings = notification.team_rankings || [];
  const warehouseReport = notification.warehouse_report || {};
  const employeeReport = notification.employee_report || {};

  return (
    <div className="mt-2">
      {/* 1. DOANH SỐ TỔNG QUAN */}
      <div className="p-2 border rounded mb-2 shadow-sm notification-summary-card">
        <div
          className="small text-muted font-weight-bolder mb-15 d-flex align-items-center"
          style={{ letterSpacing: '0.8px', fontSize: '12px' }}
        >
          <TrendingUp size={16} className="text-primary mr-50" /> DOANH SỐ TỔNG QUAN
        </div>
        <div className="row">
          {[
            { label: 'Tổng số đơn', value: formatQuantity(summary.total_orders) },
            { label: 'Tổng số tiền', value: formatCurrency(summary.total_amount) },
            {
              label: 'Khách chuyển khoản',
              value: formatCurrency(notification.bank_transfer_amount),
            },
            { label: 'COD đã thanh toán', value: formatCurrency(notification.cod_paid_amount) },
          ].map((item) => {
            const { icon, bgColor } = getMetricIconAndColor(item.label);
            return (
              <div key={item.label} className="col-md-3 col-6 mb-1 mb-md-0">
                <div
                  className="border rounded p-1 text-center h-100 d-flex flex-column align-items-center justify-content-center shadow-sm notification-sub-card"
                  style={{
                    minHeight: '100px',
                  }}
                >
                  <div
                    className="mb-50 d-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: '34px', height: '34px', backgroundColor: bgColor }}
                  >
                    {icon}
                  </div>
                  <div
                    className="small text-muted mb-25"
                    style={{ fontSize: '12px', fontWeight: '500' }}
                  >
                    {item.label}
                  </div>
                  <strong className="text-dark" style={{ fontSize: '15px' }}>
                    {item.value}
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. BÁO CÁO NHÂN SỰ */}
      <div className="p-2 border rounded mb-2 shadow-sm notification-summary-card">
        <div
          className="small text-muted font-weight-bolder mb-15 d-flex align-items-center"
          style={{ letterSpacing: '0.8px', fontSize: '12px' }}
        >
          <Users size={16} className="text-info mr-50" /> BÁO CÁO NHÂN SỰ
        </div>
        {employeeReport.status === 'available' ? (
          <div className="row">
            {[
              { label: 'Đang làm việc', value: formatQuantity(employeeReport.working_total) },
              { label: 'Mới vào', value: formatQuantity(employeeReport.new_joined_total) },
              { label: 'Nghỉ việc', value: formatQuantity(employeeReport.left_total) },
            ].map((item) => {
              const { icon, bgColor } = getMetricIconAndColor(item.label);
              return (
                <div key={item.label} className="col-md-4 col-12 mb-1 mb-md-0">
                  <div
                    className="border rounded p-1 text-center h-100 d-flex flex-column align-items-center justify-content-center shadow-sm notification-sub-card"
                    style={{
                      minHeight: '100px',
                    }}
                  >
                    <div
                      className="mb-50 d-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: '34px', height: '34px', backgroundColor: bgColor }}
                    >
                      {icon}
                    </div>
                    <div
                      className="small text-muted mb-25"
                      style={{ fontSize: '12px', fontWeight: '500' }}
                    >
                      {item.label}
                    </div>
                    <strong className="text-dark" style={{ fontSize: '15px' }}>
                      {item.value}
                    </strong>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-muted small p-1 bg-light rounded text-center">
            Không lấy được dữ liệu nhân sự
            {employeeReport.error_message ? `: ${employeeReport.error_message}` : '.'}
          </div>
        )}
      </div>

      {/* 3. DOANH SỐ THEO TEAM */}
      <div className="p-2 border rounded mb-2 shadow-sm notification-summary-card">
        <div
          className="small text-muted font-weight-bolder mb-15 d-flex align-items-center"
          style={{ letterSpacing: '0.8px', fontSize: '12px' }}
        >
          <TrendingUp size={16} className="text-success mr-50" /> DOANH SỐ THEO TEAM
        </div>
        {teamRankings.length > 0 ? (
          (() => {
            const maxAmount = Math.max(...teamRankings.map((t) => Number(t.total_amount || 0)), 1);
            return (
              <div className="row">
                {teamRankings.map((team, index) => (
                  <div
                    key={`${team.project_id || team.team_name || index}`}
                    className="col-md-6 col-12 mb-1"
                  >
                    <div className="p-1 border rounded shadow-sm h-100 notification-sub-card">
                      <div className="d-flex justify-content-between align-items-center mb-50">
                        <div>
                          <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
                            {team.team_name}
                          </span>
                          <span className="small text-muted ml-50" style={{ fontSize: '12px' }}>
                            ({formatQuantity(team.total_orders)} đơn)
                          </span>
                        </div>
                        <strong className="text-primary" style={{ fontSize: '14px' }}>
                          {formatCurrency(team.total_amount)}
                        </strong>
                      </div>
                      <div className="w-100 mt-25">
                        <div
                          className="notification-progress-bar-bg"
                          style={{
                            height: '6px',
                            borderRadius: '3px',
                            width: '100%',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              borderRadius: '3px',
                              backgroundColor: index === 0 ? '#10b981' : '#3b82f6',
                              width: `${(Number(team.total_amount || 0) / maxAmount) * 100}%`,
                              transition: 'width 0.5s ease',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
        ) : (
          <div className="text-muted small p-1 bg-light rounded text-center">
            Chưa có dữ liệu team trong ngày báo cáo.
          </div>
        )}
      </div>

      {/* 4. BÁO CÁO XUẤT NHẬP KHO */}
      <div className="p-2 border rounded shadow-sm notification-summary-card">
        <div
          className="small text-muted font-weight-bolder mb-15 d-flex align-items-center"
          style={{ letterSpacing: '0.8px', fontSize: '13px' }}
        >
          <Briefcase size={18} className="text-warning mr-50" /> BÁO CÁO XUẤT NHẬP KHO
        </div>
        <div className="row">
          {[
            { label: 'Phiếu nhập', value: formatQuantity(warehouseReport.import_documents_count) },
            { label: 'Phiếu xuất', value: formatQuantity(warehouseReport.export_documents_count) },
            {
              label: 'Phiếu điều chuyển',
              value: formatQuantity(warehouseReport.transfer_documents_count),
            },
            {
              label: 'Phiếu điều chỉnh',
              value: formatQuantity(warehouseReport.adjustment_documents_count),
            },
            { label: 'Đã ghi sổ', value: formatQuantity(warehouseReport.posted_documents_count) },
            { label: 'Tổng SL nhập', value: formatQuantity(warehouseReport.import_total_qty) },
            { label: 'Tổng SL xuất', value: formatQuantity(warehouseReport.export_total_qty) },
          ].map((item) => {
            const { icon, bgColor } = getMetricIconAndColor(item.label);
            return (
              <div key={item.label} className="col-md-3 col-6 mb-1">
                <div
                  className="border rounded p-1 text-center h-100 d-flex flex-column align-items-center justify-content-center shadow-sm notification-sub-card"
                  style={{
                    minHeight: '112px',
                  }}
                >
                  <div
                    className="mb-25 d-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: '38px', height: '38px', backgroundColor: bgColor }}
                  >
                    {icon}
                  </div>
                  <div
                    className="small text-muted mb-25"
                    style={{ fontSize: '13px', fontWeight: '600', lineHeight: '1.35' }}
                  >
                    {item.label}
                  </div>
                  <strong className="text-dark" style={{ fontSize: '18px', lineHeight: '1.2' }}>
                    {item.value}
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function NotificationDropDown() {
  const printRef = React.useRef(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [isLoadingList, setIsLoadingList] = React.useState(false);
  const [isLoadingCount, setIsLoadingCount] = React.useState(false);
  const [isMarkingAll, setIsMarkingAll] = React.useState(false);
  const [selectedNotification, setSelectedNotification] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isExportingPdf, setIsExportingPdf] = React.useState(false);
  let printDocumentTitle = 'thong-bao';

  if (selectedNotification?.report_date) {
    printDocumentTitle = `bao-cao-ngay-${dayjs(selectedNotification.report_date).format(
      'DD-MM-YYYY',
    )}`;
  }

  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printDocumentTitle,
    pageStyle: `
      @page {
        size: A4;
        margin: 16mm;
      }

      @media print {
        html, body {
          background: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body {
          margin: 0;
        }

        .notification-detail-container {
          background-color: #ffffff !important;
          color: #171725 !important;
          padding: 20px !important;
        }

        .notification-detail-container .text-dark {
          color: #171725 !important;
        }

        .notification-detail-container .text-muted {
          color: #6c757d !important;
        }

        .notification-detail-container .notification-summary-card {
          background-color: #ffffff !important;
          border-color: #eaeaea !important;
          color: #171725 !important;
          box-shadow: none !important;
        }

        .notification-detail-container .notification-sub-card {
          background-color: #fafbfc !important;
          border-color: #eaeaea !important;
          color: #171725 !important;
          box-shadow: none !important;
        }
      }
    `,
    onAfterPrint: () => {
      setIsExportingPdf(false);
    },
  });

  const handleExportPdf = React.useCallback(() => {
    setIsExportingPdf(true);
    triggerPrint();
  }, [triggerPrint]);

  const loadUnreadCount = React.useCallback(async () => {
    setIsLoadingCount(true);
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Không tải được số thông báo chưa đọc:', error);
    } finally {
      setIsLoadingCount(false);
    }
  }, []);

  const loadNotifications = React.useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getNotifications({ per_page: 10 });
      setNotifications(response?.data || []);
      setUnreadCount(response?.meta?.unread_count || 0);
    } catch (error) {
      console.error('Không tải được danh sách thông báo:', error);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  React.useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  React.useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification?.read_at) {
      try {
        await markNotificationAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((item) => {
            if (item.id === notification.id) {
              return { ...item, read_at: new Date().toISOString() };
            }

            return item;
          }),
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        console.error('Không đánh dấu đã đọc được thông báo:', error);
      }
    }

    setSelectedNotification(notification);
    setIsModalOpen(true);
  };

  const handleMarkAllAsRead = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMarkingAll(true);

    try {
      await markAllNotificationsAsRead();
      setUnreadCount(0);
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() })),
      );
    } catch (error) {
      console.error('Không đánh dấu đã đọc tất cả được:', error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const badgeText = unreadCount > 99 ? '99+' : unreadCount;
  const notificationItems = (() => {
    if (isLoadingList) {
      return (
        <DropdownItem tag="div" className="text-center py-2">
          <Spinner size="sm" />
        </DropdownItem>
      );
    }

    if (notifications.length === 0) {
      return (
        <DropdownItem tag="div" className="text-center text-muted py-2">
          Chưa có thông báo
        </DropdownItem>
      );
    }

    return notifications.map((notification) => {
      let reportDateText = '';
      let createdAtText = '';

      if (notification.report_date) {
        reportDateText = dayjs(notification.report_date).format('DD/MM/YYYY');
      }

      if (notification.created_at) {
        createdAtText = dayjs(notification.created_at).format('DD/MM/YYYY HH:mm');
      }

      const isUnread = !notification.read_at;
      const isReport = notification.title?.toLowerCase().includes('báo cáo');

      return (
        <DropdownItem
          key={notification.id}
          tag="div"
          className="py-1 px-1 border-bottom cursor-pointer notification-list-item"
          style={{
            backgroundColor: isUnread ? '#f8f9ff' : '#ffffff',
            borderLeft: isUnread ? '4px solid #3b82f6' : '4px solid transparent',
            transition: 'all 0.2s ease',
            whiteSpace: 'normal',
            display: 'block',
          }}
          onClick={() => handleNotificationClick(notification)}
        >
          <div className="d-flex align-items-start">
            {/* Left side: Beautiful Round Icon Circle */}
            <div
              className="mr-75 d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{
                width: '38px',
                height: '38px',
                backgroundColor: isReport ? '#fff3e0' : '#e3f2fd',
                color: isReport ? '#ff9800' : '#2196f3',
              }}
            >
              {isReport ? <FileText size={18} /> : <Bell size={18} />}
            </div>

            {/* Right side: Texts */}
            <div className="flex-grow-1">
              <div className="d-flex align-items-center justify-content-between mb-25">
                <span
                  className="font-weight-bolder text-dark"
                  style={{ fontSize: '13px', lineHeight: '1.3' }}
                >
                  {notification.title}
                </span>
                {isUnread && (
                  <Badge color="light-danger" pill style={{ fontSize: '10px', padding: '2px 6px' }}>
                    Mới
                  </Badge>
                )}
              </div>
              <div
                className="text-muted mb-25 text-truncate-2-lines"
                style={{
                  fontSize: '11px',
                  lineHeight: '1.4',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {notification.message}
              </div>
              <div
                className="d-flex align-items-center justify-content-between text-muted"
                style={{ fontSize: '10px' }}
              >
                <span className="d-flex align-items-center">
                  <Clock size={11} className="mr-25" />
                  {createdAtText}
                </span>
                {(reportDateText || notification.send_slot_label) && (
                  <span className="d-flex align-items-center">
                    <Calendar size={11} className="mr-25" />
                    {reportDateText}{' '}
                    {notification.send_slot_label ? `(${notification.send_slot_label})` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </DropdownItem>
      );
    });
  })();

  return (
    <>
      <Dropdown
        isOpen={isOpen}
        toggle={() => setIsOpen((prev) => !prev)}
        tag="div"
        className="dropdown-notification d-flex mx-1"
      >
        <DropdownToggle
          tag="button"
          className="border-0 bg-transparent p-0 shadow-none"
          style={{ lineHeight: 1 }}
        >
          <div className="d-flex align-items-center justify-content-center bell position-relative">
            <Bell size={21} color="#171725" />
            {(unreadCount > 0 || isLoadingCount) && (
              <Badge pill color="danger" className="badge-up">
                {isLoadingCount ? '...' : badgeText}
              </Badge>
            )}
          </div>
        </DropdownToggle>
        <DropdownMenu
          right
          className="notification-dropdown-menu"
        >
          <div
            className="d-flex align-items-center justify-content-between p-1 border-bottom"
            style={{ backgroundColor: '#fafafa' }}
          >
            <h4
              className="notification-title mb-0 font-weight-bolder text-dark"
              style={{ fontSize: '15px' }}
            >
              Thông báo
            </h4>
            <div className="d-flex align-items-center">
              <Badge
                color="light-primary"
                pill
                className="mr-75 font-weight-bold"
                style={{ padding: '4px 8px' }}
              >
                {unreadCount} mới
              </Badge>
              {unreadCount > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  className="text-primary small font-weight-bold cursor-pointer"
                  onClick={handleMarkAllAsRead}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleMarkAllAsRead(e);
                  }}
                  style={{ transition: 'color 0.2s' }}
                >
                  {isMarkingAll ? 'Đang xử lý...' : 'Đọc hết'}
                </span>
              )}
            </div>
          </div>
          <div style={{ maxHeight: '380px', overflowY: 'auto' }}>{notificationItems}</div>
        </DropdownMenu>
      </Dropdown>

      {/* Detail Modal Popup */}
      <Modal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(!isModalOpen)}
        className="modal-dialog-centered modal-lg notification-modal"
      >
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)} className="border-bottom-0">
          <div className="d-flex align-items-center">
            {selectedNotification?.title?.toLowerCase().includes('báo cáo') ? (
              <FileText size={20} className="mr-50 text-warning" />
            ) : (
              <Bell size={20} className="mr-50 text-primary" />
            )}
            <span className="font-weight-bolder text-dark" style={{ fontSize: '16px' }}>
              Chi tiết thông báo
            </span>
          </div>
        </ModalHeader>
        <ModalBody className="p-2 p-md-3">
          {selectedNotification && (
            <div ref={printRef} className="notification-detail-container">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h3 className="text-dark font-weight-bolder mb-0" style={{ fontSize: '18px' }}>
                  {selectedNotification.title}
                </h3>
                {!selectedNotification.read_at && (
                  <Badge color="danger" pill>
                    Mới
                  </Badge>
                )}
              </div>

              <div
                className="d-flex flex-wrap text-muted mb-3"
                style={{ gap: '15px', fontSize: '13px' }}
              >
                <span className="d-flex align-items-center">
                  <Clock size={14} className="mr-25 text-primary" />
                  Gửi lúc:{' '}
                  <strong className="ml-25 text-dark">
                    {dayjs(selectedNotification.created_at).format('DD/MM/YYYY HH:mm')}
                  </strong>
                </span>
                {selectedNotification.report_date && (
                  <span className="d-flex align-items-center">
                    <Calendar size={14} className="mr-25 text-warning" />
                    Ngày báo cáo:{' '}
                    <strong className="ml-25 text-dark">
                      {dayjs(selectedNotification.report_date).format('DD/MM/YYYY')}
                    </strong>
                  </span>
                )}
                {selectedNotification.send_slot_label && (
                  <span className="d-flex align-items-center">
                    <Info size={14} className="mr-25 text-success" />
                    Khung gửi:{' '}
                    <strong className="ml-25 text-dark">
                      {selectedNotification.send_slot_label}
                    </strong>
                  </span>
                )}
              </div>

              <div
                className="p-2 mb-2 border rounded shadow-sm notification-summary-card"
                style={{
                  borderLeft: '4px solid #fba92c',
                }}
              >
                <span
                  className="font-weight-bolder text-uppercase d-block mb-1"
                  style={{ fontSize: '11px', letterSpacing: '0.8px', color: '#ff9800' }}
                >
                  Tóm tắt nhanh
                </span>
                {parseNotificationMessage(selectedNotification.message)}
              </div>

              {renderReportDetails(selectedNotification)}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <div>
            {selectedNotification?.read_at ? (
              <span className="text-muted d-flex align-items-center" style={{ fontSize: '13px' }}>
                <CheckCircle size={16} className="text-success mr-25" /> Đã đọc
              </span>
            ) : null}
          </div>
          <div className="d-flex" style={{ gap: '10px' }}>
            {isAdminDailyReport(selectedNotification) && (
              <Button
                color="primary"
                onClick={handleExportPdf}
                className="d-flex align-items-center"
                disabled={isExportingPdf}
              >
                <FileText size={16} className="mr-50" />
                {isExportingPdf ? 'Đang chuẩn bị...' : 'Export PDF'}
              </Button>
            )}
            <Button color="secondary" outline onClick={() => setIsModalOpen(false)}>
              Đóng
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default NotificationDropDown;
